import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Check, Sparkles, Sliders, Clock, Newspaper, Target, Trash2, Copy, Plus, Save, X } from "lucide-react";
import { api } from "@/lib/api";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

const PATTERN_COLORS = {
  FVG: "#3b82f6",
  BOS: "#10b981",
  CHoCH: "#f59e0b",
  LiquiditySweep: "#a78bfa",
};

const ALL_TFS = ["M1", "M5", "M15", "M30", "H1", "H4", "D1"];
const ALL_SESSIONS = ["London", "NY", "London-NY-Overlap", "Asian"];

const Chip = ({ active, onClick, children, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`px-2.5 py-1 rounded-full text-[10px] font-mono uppercase tracking-wider border transition-colors ${
      active
        ? "border-[#3b82f6] bg-[#3b82f6]/15 text-[#3b82f6]"
        : "border-[#1f2937] bg-transparent text-[#6b7280] hover:text-[#9ca3af] hover:border-[#374151]"
    }`}
  >
    {children}
  </button>
);

const ProfileCard = ({ p, isActive, onActivate, onDuplicate, onDelete }) => (
  <div
    className={`vx-card p-5 flex flex-col gap-3 transition-all ${
      isActive ? "border-[#3b82f6] shadow-[0_0_0_1px_#3b82f6]" : ""
    }`}
    data-testid={`profile-card-${p.profile_id}`}
  >
    <div className="flex items-start justify-between gap-2">
      <div>
        <div className="flex items-center gap-2">
          <div className="font-heading text-lg font-bold">{p.display_name}</div>
          {p.is_system_default && (
            <span className="text-[9px] font-mono uppercase tracking-wider text-[#6b7280] border border-[#1f2937] rounded px-1.5 py-0.5">
              system
            </span>
          )}
          {p.strategy_type === "straddle" && (
            <span
              data-testid={`straddle-pill-${p.profile_id}`}
              className="text-[9px] font-mono uppercase tracking-wider text-[#a78bfa] border border-[#a78bfa]/60 rounded px-1.5 py-0.5 bg-[#a78bfa]/10 flex items-center gap-1"
            >
              <span className="w-1 h-1 rounded-full bg-[#a78bfa] animate-pulse" /> straddle
            </span>
          )}
          {isActive && (
            <span
              className="text-[9px] font-mono uppercase tracking-wider text-[#10b981] border border-[#10b981] rounded px-1.5 py-0.5 flex items-center gap-1"
              data-testid={`active-tag-${p.profile_id}`}
            >
              <Check className="w-3 h-3" strokeWidth={3} /> active
            </span>
          )}
        </div>
        <p className="text-xs text-[#9ca3af] mt-1 leading-relaxed">{p.description}</p>
      </div>
    </div>

    <div>
      <div className="vx-label mb-1.5">Pattern weights</div>
      <div className="grid grid-cols-4 gap-1.5">
        {Object.entries(p.pattern_weights || {}).map(([k, v]) => (
          <div key={k} className="text-center">
            <div
              className="rounded text-[10px] font-mono py-1 border font-bold"
              style={{
                background: `${PATTERN_COLORS[k] || "#3b82f6"}22`,
                borderColor: `${PATTERN_COLORS[k] || "#3b82f6"}66`,
                color: PATTERN_COLORS[k] || "#3b82f6",
              }}
            >
              {k === "LiquiditySweep" ? "SWEEP" : k}
            </div>
            <div className="text-[10px] font-mono mt-1 text-[#9ca3af]">{Number(v).toFixed(1)}×</div>
          </div>
        ))}
      </div>
    </div>

    <div className="grid grid-cols-3 gap-2 text-[10px] font-mono">
      <div className="flex flex-col">
        <span className="text-[#6b7280] uppercase tracking-wider">Min Conf</span>
        <span className="text-[#e5e7eb] font-bold text-sm mt-0.5">{p.min_confidence}</span>
      </div>
      <div className="flex flex-col">
        <span className="text-[#6b7280] uppercase tracking-wider">RR Target</span>
        <span className="text-[#e5e7eb] font-bold text-sm mt-0.5">1 : {p.rr_target}</span>
      </div>
      <div className="flex flex-col">
        <span className="text-[#6b7280] uppercase tracking-wider">News win</span>
        <span className="text-[#e5e7eb] font-bold text-sm mt-0.5">{p.news_avoidance_minutes}m</span>
      </div>
    </div>

    <div className="flex flex-wrap gap-1">
      {(p.preferred_timeframes || []).map((t) => (
        <span key={t} className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-[#1f2937] text-[#9ca3af]">
          {t}
        </span>
      ))}
    </div>

    {p.trade_management_override && (
      <div className="pt-3 border-t border-[#1f2937]" data-testid={`tm-override-${p.profile_id}`}>
        <div className="vx-label mb-1.5 flex items-center gap-1">
          <Sparkles className="w-3 h-3" /> Trade Management
        </div>
        <div className="flex flex-wrap gap-1.5">
          {p.trade_management_override.break_even_enabled ? (
            <span className="text-[9px] font-mono px-1.5 py-0.5 rounded border border-[#10b981]/60 text-[#10b981] bg-[#10b981]/10">
              BE {p.trade_management_override.break_even_trigger_pips}p / +{p.trade_management_override.break_even_offset_pips}p
            </span>
          ) : (
            <span className="text-[9px] font-mono px-1.5 py-0.5 rounded border border-[#1f2937] text-[#6b7280]">BE off</span>
          )}
          {p.trade_management_override.trailing_stop_enabled ? (
            <span className="text-[9px] font-mono px-1.5 py-0.5 rounded border border-[#3b82f6]/60 text-[#3b82f6] bg-[#3b82f6]/10">
              TS {p.trade_management_override.trailing_stop_trigger_pips}p / {p.trade_management_override.trailing_stop_distance_pips}p
            </span>
          ) : (
            <span className="text-[9px] font-mono px-1.5 py-0.5 rounded border border-[#1f2937] text-[#6b7280]">TS off</span>
          )}
          {p.trade_management_override.partial_close_enabled && (p.trade_management_override.partial_close_levels || []).length > 0 ? (
            <span className="text-[9px] font-mono px-1.5 py-0.5 rounded border border-[#f59e0b]/60 text-[#f59e0b] bg-[#f59e0b]/10">
              PC {(p.trade_management_override.partial_close_levels || []).map((lv) => `${lv.close_pct}%@${lv.trigger_pips}p`).join(",")}
            </span>
          ) : (
            <span className="text-[9px] font-mono px-1.5 py-0.5 rounded border border-[#1f2937] text-[#6b7280]">PC off</span>
          )}
        </div>
      </div>
    )}

    {p.strategy_type === "straddle" && p.straddle_config && (
      <div className="pt-3 border-t border-[#1f2937]" data-testid={`straddle-config-${p.profile_id}`}>
        <div className="vx-label mb-1.5 flex items-center gap-1 text-[#a78bfa]">
          <span className="w-1.5 h-1.5 rounded-full bg-[#a78bfa]" /> Straddle Configuration
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[10px] font-mono">
          <div>
            <div className="text-[#6b7280]">Lot each side</div>
            <div className="text-white font-bold">{p.straddle_config.lot_each_side}</div>
          </div>
          <div>
            <div className="text-[#6b7280]">Wait window</div>
            <div className="text-white font-bold">{Math.round((p.straddle_config.wait_window_seconds || 900) / 60)}min</div>
          </div>
          <div>
            <div className="text-[#6b7280]">AI threshold</div>
            <div className="text-white font-bold">{p.straddle_config.ai_confidence_threshold}%</div>
          </div>
          <div>
            <div className="text-[#6b7280]">Multi-TF gate</div>
            <div className={`font-bold ${p.straddle_config.require_multi_tf_alignment ? "text-[#10b981]" : "text-[#6b7280]"}`}>
              {p.straddle_config.require_multi_tf_alignment ? "ON" : "off"}
            </div>
          </div>
        </div>
      </div>
    )}

    <div className="mt-1 pt-3 border-t border-[#1f2937] flex items-center gap-2">
      <button
        onClick={() => onActivate(p)}
        disabled={isActive}
        data-testid={`activate-btn-${p.profile_id}`}
        className={`flex-1 h-9 text-xs font-mono uppercase tracking-wider rounded transition-colors ${
          isActive
            ? "bg-[#10b981]/15 text-[#10b981] cursor-default"
            : "bg-[#3b82f6] hover:bg-[#2563eb] text-white"
        }`}
      >
        {isActive ? "Active" : "Activate"}
      </button>
      <button
        onClick={() => onDuplicate(p)}
        data-testid={`duplicate-btn-${p.profile_id}`}
        title="Duplicate & customize"
        className="w-9 h-9 rounded border border-[#1f2937] hover:border-[#374151] hover:bg-[#1f2937] flex items-center justify-center text-[#9ca3af]"
      >
        <Copy className="w-3.5 h-3.5" />
      </button>
      {!p.is_system_default && (
        <button
          onClick={() => onDelete(p)}
          data-testid={`delete-btn-${p.profile_id}`}
          title="Delete custom profile"
          className="w-9 h-9 rounded border border-[#1f2937] hover:border-[#ef4444] hover:bg-[#ef4444]/10 flex items-center justify-center text-[#ef4444]"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  </div>
);

const CustomizeDialog = ({ open, seed, onClose, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (seed) {
      setForm({
        profile_id: `${seed.profile_id}_custom`,
        display_name: `${seed.display_name} (Custom)`,
        description: seed.description || "",
        pattern_weights: { ...(seed.pattern_weights || { FVG: 0.8, BOS: 0.8, CHoCH: 0.8, LiquiditySweep: 0.8 }) },
        min_confidence: seed.min_confidence ?? 60,
        rr_target: seed.rr_target ?? 2,
        preferred_timeframes: [...(seed.preferred_timeframes || ["M15", "H1"])],
        preferred_sessions: [...(seed.preferred_sessions || ALL_SESSIONS)],
        news_avoidance_minutes: seed.news_avoidance_minutes ?? 30,
        risk_per_trade_pct_override: seed.risk_per_trade_pct_override ?? null,
        // Phase 7A — straddle config (only meaningful when strategy_type === "straddle")
        strategy_type: seed.strategy_type || "single",
        straddle_config: seed.strategy_type === "straddle"
          ? {
              lot_each_side:               seed.straddle_config?.lot_each_side ?? 0.01,
              wait_window_seconds:         seed.straddle_config?.wait_window_seconds ?? 900,
              ai_confidence_threshold:     seed.straddle_config?.ai_confidence_threshold ?? 80,
              max_hold_seconds_if_undecided: seed.straddle_config?.max_hold_seconds_if_undecided ?? (seed.straddle_config?.wait_window_seconds ?? 900),
              close_both_if_undecided:     seed.straddle_config?.close_both_if_undecided ?? true,
              require_multi_tf_alignment:  seed.straddle_config?.require_multi_tf_alignment ?? true,
            }
          : null,
      });
    }
  }, [seed]);

  if (!form) return null;

  const setW = (k, v) => setForm((f) => ({ ...f, pattern_weights: { ...f.pattern_weights, [k]: v } }));
  const toggleList = (k, val) => setForm((f) => {
    const list = f[k].includes(val) ? f[k].filter((x) => x !== val) : [...f[k], val];
    return { ...f, [k]: list };
  });

  const save = async () => {
    if (!form.profile_id.trim() || form.profile_id.length < 3) {
      toast.error("Profile ID must be at least 3 characters");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        profile_id: form.profile_id.toLowerCase().replace(/\s+/g, "_"),
      };
      // Phase 7A — omit straddle_config for non-straddle profiles so the backend
      // doesn't persist a stale null block.
      if (payload.strategy_type !== "straddle") {
        delete payload.straddle_config;
      }
      await api.post("/strategies/custom", payload);
      toast.success(`Profile "${payload.display_name}" created`);
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-2xl bg-[#0a0e1a] border-[#1f2937] text-[#e5e7eb]"
        data-testid="customize-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-xl tracking-tight">Customize Strategy Profile</DialogTitle>
          <DialogDescription className="text-xs text-[#9ca3af]">
            Tune pattern weights, timeframe filters and session penalties for this profile.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="vx-label mb-1 block">Profile ID</label>
              <Input
                value={form.profile_id}
                onChange={(e) => setForm({ ...form, profile_id: e.target.value })}
                data-testid="custom-profile-id"
                className="bg-[#111827] border-[#1f2937] font-mono text-xs"
              />
            </div>
            <div>
              <label className="vx-label mb-1 block">Display name</label>
              <Input
                value={form.display_name}
                onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                data-testid="custom-display-name"
                className="bg-[#111827] border-[#1f2937] text-xs"
              />
            </div>
          </div>

          <div>
            <label className="vx-label mb-1 block">Description</label>
            <Input
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="bg-[#111827] border-[#1f2937] text-xs"
            />
          </div>

          <div>
            <div className="vx-label mb-2 block flex items-center gap-1">
              <Sliders className="w-3 h-3" /> Pattern weights (0 = disabled)
            </div>
            <div className="space-y-3">
              {Object.entries(form.pattern_weights).map(([k, v]) => (
                <div key={k} className="grid grid-cols-[100px_1fr_60px] items-center gap-3">
                  <span
                    className="text-[11px] font-mono font-bold uppercase"
                    style={{ color: PATTERN_COLORS[k] || "#3b82f6" }}
                  >
                    {k === "LiquiditySweep" ? "SWEEP" : k}
                  </span>
                  <Slider
                    value={[v]}
                    min={0}
                    max={1.5}
                    step={0.1}
                    onValueChange={(a) => setW(k, a[0])}
                    data-testid={`weight-slider-${k}`}
                  />
                  <span className="font-mono text-xs text-right">{Number(v).toFixed(1)}×</span>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="vx-label mb-1 block">Min confidence</label>
              <div className="flex items-center gap-2">
                <Slider
                  value={[form.min_confidence]}
                  min={0}
                  max={100}
                  step={5}
                  onValueChange={(a) => setForm({ ...form, min_confidence: a[0] })}
                />
                <span className="w-12 text-right font-mono text-xs">{form.min_confidence}</span>
              </div>
            </div>
            <div>
              <label className="vx-label mb-1 block">RR target</label>
              <div className="flex items-center gap-2">
                <Slider
                  value={[form.rr_target]}
                  min={0.5}
                  max={5}
                  step={0.1}
                  onValueChange={(a) => setForm({ ...form, rr_target: Number(a[0].toFixed(1)) })}
                />
                <span className="w-12 text-right font-mono text-xs">1:{form.rr_target}</span>
              </div>
            </div>
            <div>
              <label className="vx-label mb-1 block">News avoid (min)</label>
              <div className="flex items-center gap-2">
                <Slider
                  value={[form.news_avoidance_minutes]}
                  min={0}
                  max={120}
                  step={5}
                  onValueChange={(a) => setForm({ ...form, news_avoidance_minutes: a[0] })}
                />
                <span className="w-12 text-right font-mono text-xs">{form.news_avoidance_minutes}</span>
              </div>
            </div>
          </div>

          <div>
            <div className="vx-label mb-2 block flex items-center gap-1">
              <Clock className="w-3 h-3" /> Preferred timeframes
            </div>
            <div className="flex flex-wrap gap-1.5">
              {ALL_TFS.map((tf) => (
                <Chip
                  key={tf}
                  active={form.preferred_timeframes.includes(tf)}
                  onClick={() => toggleList("preferred_timeframes", tf)}
                >
                  {tf}
                </Chip>
              ))}
            </div>
          </div>

          <div>
            <div className="vx-label mb-2 block flex items-center gap-1">
              <Target className="w-3 h-3" /> Preferred sessions
            </div>
            <div className="flex flex-wrap gap-1.5">
              {ALL_SESSIONS.map((s) => (
                <Chip
                  key={s}
                  active={form.preferred_sessions.includes(s)}
                  onClick={() => toggleList("preferred_sessions", s)}
                >
                  {s}
                </Chip>
              ))}
            </div>
          </div>

          {form.strategy_type === "straddle" && form.straddle_config && (
            <div
              className="pt-4 mt-4 border-t border-[#a78bfa]/30 space-y-4"
              data-testid="straddle-config-section"
            >
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[#a78bfa]" />
                <span className="text-xs uppercase tracking-widest text-[#a78bfa] font-bold">
                  Straddle Configuration
                </span>
                <span className="text-[9px] font-mono uppercase tracking-wider text-[#6b7280] border border-[#1f2937] rounded px-1.5 py-0.5">
                  Phase 7A
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="vx-label mb-1 flex items-center justify-between">
                    <span>Lot each side</span>
                    <span className="text-[10px] font-mono text-[#6b7280]">
                      total = {(form.straddle_config.lot_each_side * 2).toFixed(2)} lots
                    </span>
                  </div>
                  <Input
                    type="number"
                    step={0.01}
                    min={0.01}
                    value={form.straddle_config.lot_each_side}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        straddle_config: {
                          ...f.straddle_config,
                          lot_each_side: parseFloat(e.target.value) || 0.01,
                        },
                      }))
                    }
                    data-testid="straddle-lot-input"
                    className="h-9 font-mono text-sm"
                  />
                </div>

                <div>
                  <div className="vx-label mb-1 flex items-center justify-between">
                    <span>Wait window (seconds)</span>
                    <span className="text-[10px] font-mono text-[#6b7280]">
                      = {(form.straddle_config.wait_window_seconds / 60).toFixed(1)} min
                    </span>
                  </div>
                  <Input
                    type="number"
                    step={60}
                    min={60}
                    max={7200}
                    value={form.straddle_config.wait_window_seconds}
                    onChange={(e) => {
                      const v = parseInt(e.target.value, 10) || 60;
                      setForm((f) => ({
                        ...f,
                        straddle_config: {
                          ...f.straddle_config,
                          wait_window_seconds: v,
                          // Keep max_hold in lockstep with wait_window unless user overrode it.
                          max_hold_seconds_if_undecided: v,
                        },
                      }));
                    }}
                    data-testid="straddle-wait-input"
                    className="h-9 font-mono text-sm"
                  />
                </div>
              </div>

              <div>
                <div className="vx-label mb-2 flex items-center justify-between">
                  <span>AI confidence threshold</span>
                  <span className="text-sm font-mono font-bold text-[#a78bfa]">
                    {form.straddle_config.ai_confidence_threshold}%
                  </span>
                </div>
                <Slider
                  min={60}
                  max={95}
                  step={1}
                  value={[form.straddle_config.ai_confidence_threshold]}
                  onValueChange={(v) =>
                    setForm((f) => ({
                      ...f,
                      straddle_config: { ...f.straddle_config, ai_confidence_threshold: v[0] },
                    }))
                  }
                  data-testid="straddle-conf-slider"
                />
                <div className="text-[10px] font-mono text-[#6b7280] mt-1">
                  AI must be at least {form.straddle_config.ai_confidence_threshold}% confident before the losing leg is closed.
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label
                  className="flex items-center justify-between rounded border border-[#1f2937] bg-[#0a0e1a] p-3 cursor-pointer hover:border-[#374151]"
                  data-testid="straddle-close-both-label"
                >
                  <div>
                    <div className="text-sm font-semibold">Close both if undecided</div>
                    <div className="text-[10px] text-[#6b7280] font-mono mt-0.5">
                      If OFF, both legs continue with full TM (hedged mode).
                    </div>
                  </div>
                  <Switch
                    checked={!!form.straddle_config.close_both_if_undecided}
                    onCheckedChange={(v) =>
                      setForm((f) => ({
                        ...f,
                        straddle_config: { ...f.straddle_config, close_both_if_undecided: v },
                      }))
                    }
                    data-testid="straddle-close-both-toggle"
                  />
                </label>

                <label
                  className="flex items-center justify-between rounded border border-[#1f2937] bg-[#0a0e1a] p-3 cursor-pointer hover:border-[#374151]"
                  data-testid="straddle-multi-tf-label"
                >
                  <div>
                    <div className="text-sm font-semibold">Require multi-TF alignment</div>
                    <div className="text-[10px] text-[#6b7280] font-mono mt-0.5">
                      M15, H1, H4 must all agree before resolution.
                    </div>
                  </div>
                  <Switch
                    checked={!!form.straddle_config.require_multi_tf_alignment}
                    onCheckedChange={(v) =>
                      setForm((f) => ({
                        ...f,
                        straddle_config: { ...f.straddle_config, require_multi_tf_alignment: v },
                      }))
                    }
                    data-testid="straddle-multi-tf-toggle"
                  />
                </label>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <button
            onClick={onClose}
            className="h-9 px-4 rounded border border-[#1f2937] hover:bg-[#1f2937] text-sm text-[#9ca3af] flex items-center gap-1"
            data-testid="cancel-custom-btn"
          >
            <X className="w-3.5 h-3.5" /> Cancel
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="save-custom-btn"
            className="h-9 px-5 rounded bg-[#3b82f6] hover:bg-[#2563eb] text-white text-sm flex items-center gap-1 disabled:opacity-60"
          >
            <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save Profile"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function Strategies() {
  const [profiles, setProfiles] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [customizeSeed, setCustomizeSeed] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [p, a] = await Promise.all([
        api.get("/strategies"),
        api.get("/strategies/active"),
      ]);
      setProfiles(p.data.profiles || []);
      setActiveId(a.data?.profile_id || null);
    } catch (e) {
      toast.error("Failed to load strategy profiles");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const activate = async (p) => {
    try {
      await api.put("/strategies/active", { profile_id: p.profile_id });
      setActiveId(p.profile_id);
      toast.success(`"${p.display_name}" is now active`);
    } catch (e) {
      toast.error("Failed to activate profile");
    }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete "${p.display_name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/strategies/${p.profile_id}`);
      toast.success("Profile deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  const systemProfiles = useMemo(() => profiles.filter((p) => p.is_system_default), [profiles]);
  const customProfiles = useMemo(() => profiles.filter((p) => !p.is_system_default), [profiles]);

  return (
    <div className="space-y-6" data-testid="strategies-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="vx-label">Strategy</div>
          <h1 className="font-heading text-3xl md:text-4xl font-black tracking-tighter mt-1">
            Strategy Profiles
          </h1>
          <p className="text-sm text-[#9ca3af] mt-2 max-w-2xl">
            Each profile re-shapes signal confidence: pattern weights, timeframe filters, session penalties,
            RR target and news avoidance window. The active profile applies to every new signal generated.
          </p>
        </div>
        <div className="flex items-center gap-2 px-3 py-2 rounded border border-[#3b82f6]/40 bg-[#3b82f6]/10">
          <Sparkles className="w-3.5 h-3.5 text-[#3b82f6]" />
          <div className="text-xs">
            <span className="text-[#9ca3af]">Active:</span>{" "}
            <span className="font-bold text-[#3b82f6] font-mono" data-testid="active-profile-name">
              {activeId || "—"}
            </span>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-[#9ca3af] font-mono text-sm">Loading profiles…</div>
      ) : (
        <>
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="vx-label" data-testid="system-profiles-header">
                System Strategies ({systemProfiles.length})
              </div>
              <div className="text-[11px] text-[#6b7280] font-mono">read-only · duplicate to customize</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {systemProfiles.map((p) => (
                <ProfileCard
                  key={p.profile_id}
                  p={p}
                  isActive={activeId === p.profile_id}
                  onActivate={activate}
                  onDuplicate={setCustomizeSeed}
                  onDelete={del}
                />
              ))}
            </div>
          </section>

          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="vx-label" data-testid="custom-profiles-header">
                Your Custom Strategies ({customProfiles.length})
              </div>
              <button
                onClick={() =>
                  setCustomizeSeed(
                    profiles.find((p) => p.profile_id === "balanced") || systemProfiles[0]
                  )
                }
                data-testid="new-custom-btn"
                className="h-8 px-3 rounded text-xs font-mono border border-[#3b82f6]/40 text-[#3b82f6] hover:bg-[#3b82f6]/10 flex items-center gap-1"
              >
                <Plus className="w-3 h-3" /> New from Balanced
              </button>
            </div>
            {customProfiles.length === 0 ? (
              <div className="vx-card p-6 text-center text-xs text-[#6b7280] font-mono border-dashed">
                No custom profiles yet. Duplicate a system profile to tune your own.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {customProfiles.map((p) => (
                  <ProfileCard
                    key={p.profile_id}
                    p={p}
                    isActive={activeId === p.profile_id}
                    onActivate={activate}
                    onDuplicate={setCustomizeSeed}
                    onDelete={del}
                  />
                ))}
              </div>
            )}
          </section>
        </>
      )}

      <CustomizeDialog
        open={!!customizeSeed}
        seed={customizeSeed}
        onClose={() => setCustomizeSeed(null)}
        onSaved={load}
      />
    </div>
  );
}
